/**
 * H3 — Browser error beacon.
 *
 * Captures uncaught browser errors (`window.onerror`) and unhandled promise
 * rejections (`window.onunhandledrejection`) and POSTs a small, scrubbed,
 * size-capped payload to the SAME-ORIGIN hardened backend proxy at
 * `/api/p/v1/_obs/client-error` (served by z.oxbo.energy → oxbo-p).
 *
 * The proxy is the only thing that talks to reports.roderickc.com — the
 * REPORTS_API_KEY is server-side-only and NEVER ships to the browser, so this
 * module MUST NOT reference reports.roderickc.com in any form.
 *
 * Dark-safe: when the backend H3 flag (OBS_CLIENT_ERROR_ENABLED) is off the
 * proxy answers 204 and forwards nothing, so installing the beacon is harmless.
 *
 * Contract with the server: the browser sends raw-ish fields; the SERVER
 * normalizes and owns `fingerprint` / `firstSeen` / `lastSeen` / `timestamp`.
 * The client only does a best-effort scrub + dedupe + size cap — the server
 * re-scrubs and is the source of truth.
 *
 * MUST NEVER THROW: every entry point is wrapped in try/catch and the beacon
 * guards against its own errors re-triggering `window.onerror` (which would
 * otherwise create a report loop).
 */

/** Same-origin backend proxy. NEVER reports.roderickc.com (key stays server-side). */
const BEACON_PATH = "/api/p/v1/_obs/client-error";

/** Hard cap on the serialized JSON body. The server also enforces ≤8k. */
const MAX_BODY_BYTES = 8 * 1024;

/** Dedupe window: suppress an identical (message+stack+url) within this many ms. */
const DEDUPE_WINDOW_MS = 10_000;

/** Bound the dedupe map so a flood of distinct errors can't grow it without limit. */
const MAX_DEDUPE_KEYS = 200;

type Level = "error";

interface BeaconPayload {
  message: string;
  stack: string;
  url: string;
  filename: string;
  lineno: number;
  colno: number;
  userAgent: string;
  release: string;
  level: Level;
}

let installed = false;
/** Reentrancy guard: true while we are inside our own beacon code path. */
let sending = false;
/** key -> last-sent epoch ms. */
const recent = new Map<string, number>();

/** Best-effort scrub. The server re-scrubs and is authoritative — do NOT rely on this. */
function scrub(value: string): string {
  if (!value) return "";
  let out = value;
  try {
    // Bearer tokens.
    out = out.replace(/Bearer\s+[A-Za-z0-9._~+/-]+=*/gi, "Bearer <redacted>");
    // token/key/secret/password query params or key=value pairs.
    out = out.replace(
      /\b(token|key|secret|password|passwd|pwd|api[_-]?key|access[_-]?token)\b\s*[=:]\s*[^\s&"']{1,512}/gi,
      "$1=<redacted>",
    );
    // Email addresses.
    out = out.replace(
      /\b[A-Za-z0-9._%+-]{1,128}@[A-Za-z0-9.-]{1,128}\.[A-Za-z]{2,24}\b/g,
      "<redacted>",
    );
  } catch {
    // A scrub failure must never break the beacon.
    return value;
  }
  return out;
}

/** Strip query + fragment from a URL so we don't ship secrets in the querystring. */
function stripUrl(raw: string): string {
  if (!raw) return "";
  try {
    const u = new URL(raw, "http://local.invalid");
    // Reconstruct without search/hash. For relative inputs origin is the placeholder.
    const base = `${u.origin}${u.pathname}`;
    return base.startsWith("http://local.invalid") ? u.pathname : base;
  } catch {
    // Fallback: cut at the first ? or #.
    return raw.replace(/[?#].*$/, "");
  }
}

function readRelease(): string {
  try {
    const env = import.meta.env as Record<string, string | undefined>;
    return env.VITE_RELEASE ?? env.VITE_APP_VERSION ?? "";
  } catch {
    return "";
  }
}

function safeStr(v: unknown): string {
  if (typeof v === "string") return v;
  if (v == null) return "";
  try {
    return String(v);
  } catch {
    return "";
  }
}

function safeNum(v: unknown): number {
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
}

/** Extract {message, stack} from an arbitrary error-ish value. */
function fromError(err: unknown): { message: string; stack: string } {
  if (err instanceof Error) {
    return { message: safeStr(err.message), stack: safeStr(err.stack) };
  }
  if (err && typeof err === "object") {
    const o = err as { message?: unknown; stack?: unknown };
    return { message: safeStr(o.message), stack: safeStr(o.stack) };
  }
  return { message: safeStr(err), stack: "" };
}

/** Build the scrubbed payload. */
function buildPayload(input: {
  message: string;
  stack: string;
  url: string;
  filename: string;
  lineno: number;
  colno: number;
}): BeaconPayload {
  return {
    message: scrub(input.message),
    stack: scrub(input.stack),
    url: stripUrl(input.url),
    filename: stripUrl(input.filename),
    lineno: safeNum(input.lineno),
    colno: safeNum(input.colno),
    userAgent: typeof navigator !== "undefined" ? safeStr(navigator.userAgent) : "",
    release: readRelease(),
    level: "error",
  };
}

/** Serialize with a hard <8k cap; progressively truncate the stack, then message. */
function serializeCapped(payload: BeaconPayload): string {
  let body = JSON.stringify(payload);
  if (byteLength(body) <= MAX_BODY_BYTES) return body;

  const trimmed: BeaconPayload = { ...payload };
  // Truncate the stack first (usually the biggest field).
  for (let budget = 2048; budget >= 0 && byteLength(body) > MAX_BODY_BYTES; budget = Math.floor(budget / 2)) {
    trimmed.stack = payload.stack.slice(0, budget);
    body = JSON.stringify(trimmed);
    if (budget === 0) break;
  }
  if (byteLength(body) <= MAX_BODY_BYTES) return body;

  // Still too big → drop stack entirely and clamp the message.
  trimmed.stack = "";
  trimmed.message = payload.message.slice(0, 1024);
  body = JSON.stringify(trimmed);
  if (byteLength(body) <= MAX_BODY_BYTES) return body;

  // Last resort: a minimal, guaranteed-small body.
  return JSON.stringify({
    message: payload.message.slice(0, 256),
    stack: "",
    url: payload.url,
    filename: "",
    lineno: payload.lineno,
    colno: payload.colno,
    userAgent: "",
    release: payload.release,
    level: "error",
  });
}

function byteLength(s: string): number {
  try {
    return new TextEncoder().encode(s).length;
  } catch {
    return s.length;
  }
}

/** True if this (message+stack+url) was sent within the dedupe window. */
function isDuplicate(payload: BeaconPayload, now: number): boolean {
  const key = `${payload.message} ${payload.stack} ${payload.url}`;
  const last = recent.get(key);
  if (last != null && now - last < DEDUPE_WINDOW_MS) return true;
  // Record / refresh.
  recent.set(key, now);
  // Bound the map: evict the oldest-inserted key (Map preserves insertion order).
  if (recent.size > MAX_DEDUPE_KEYS) {
    const oldest = recent.keys().next().value;
    if (oldest !== undefined) recent.delete(oldest);
  }
  return false;
}

/** POST the body via sendBeacon, falling back to keepalive fetch. Swallows all errors. */
function post(body: string): void {
  try {
    if (
      typeof navigator !== "undefined" &&
      typeof navigator.sendBeacon === "function"
    ) {
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon(BEACON_PATH, blob);
      if (ok) return;
    }
  } catch {
    // fall through to fetch
  }
  try {
    if (typeof fetch === "function") {
      void fetch(BEACON_PATH, {
        method: "POST",
        keepalive: true,
        credentials: "omit",
        headers: { "Content-Type": "application/json" },
        body,
      }).catch(() => {
        /* swallow: a beacon failure must never surface */
      });
    }
  } catch {
    // swallow
  }
}

/** Core report path. Guarded so it can never throw and never loop. */
function report(input: {
  message: string;
  stack: string;
  url: string;
  filename: string;
  lineno: number;
  colno: number;
}): void {
  if (sending) return; // reentrancy guard: don't report errors from our own code
  sending = true;
  try {
    const payload = buildPayload(input);
    if (!payload.message && !payload.stack) return; // nothing useful to send
    const now = Date.now();
    if (isDuplicate(payload, now)) return;
    const body = serializeCapped(payload);
    post(body);
  } catch {
    // MUST NEVER THROW.
  } finally {
    sending = false;
  }
}

function onError(event: ErrorEvent): void {
  try {
    const url = typeof location !== "undefined" ? location.href : "";
    const fromEvt = fromError(event.error);
    report({
      message: fromEvt.message || safeStr(event.message),
      stack: fromEvt.stack,
      url,
      filename: safeStr(event.filename),
      lineno: safeNum(event.lineno),
      colno: safeNum(event.colno),
    });
  } catch {
    // swallow
  }
}

function onRejection(event: PromiseRejectionEvent): void {
  try {
    const url = typeof location !== "undefined" ? location.href : "";
    const fromRej = fromError(event.reason);
    report({
      message: fromRej.message || "Unhandled promise rejection",
      stack: fromRej.stack,
      url,
      filename: "",
      lineno: 0,
      colno: 0,
    });
  } catch {
    // swallow
  }
}

/**
 * Install the global error listeners. Idempotent — a second call is a no-op.
 * Never throws.
 */
export function installErrorBeacon(): void {
  try {
    if (installed) return;
    if (typeof window === "undefined" || typeof window.addEventListener !== "function") return;
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    installed = true;
  } catch {
    // MUST NEVER THROW.
  }
}

/** Test-only: reset module state between tests. */
export function __resetErrorBeaconForTests(): void {
  installed = false;
  sending = false;
  recent.clear();
  try {
    if (typeof window !== "undefined" && typeof window.removeEventListener === "function") {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    }
  } catch {
    // ignore
  }
}

/** Test-only: exercise the payload builder / dedupe / serialize directly. */
export const __internals = {
  buildPayload,
  serializeCapped,
  isDuplicate,
  scrub,
  stripUrl,
  BEACON_PATH,
  MAX_BODY_BYTES,
};
